/**
 * Centralized canvas-render requests so non-renderer code (stores,
 * services, components) doesn't call Fabric's render methods directly.
 *
 * Async helpers (`requestX`) draw every requested canvas together in the
 * next animation frame — the right choice for UI updates. The sync helper
 * (`renderCanvasNow`) is for export / snapshot paths that mutate Fabric and
 * immediately read pixels (e.g. toDataURL); UI flows should NEVER call it.
 */
import type { Canvas, StaticCanvas } from 'fabric';
import { useEditorStore } from '@/stores/editorStore';

/**
 * Async (UI flows)
 */

const pendingRenders = new Set<StaticCanvas>();
const animatingRenders = new Set<StaticCanvas>();
let renderFrame = 0;

// first draws (caching objects, decoding images) are slow; past this the rest wait
const FRAME_BUDGET_MS = 10;

/**
 * Draw the canvas in the next animation frame. Requests from every canvas
 * share one frame callback instead of one each, which playback asks for on
 * every frame for every visible ad.
 *
 * Once a frame's budget is used, the remaining canvases wait for the next
 * frame, so a batch of first draws on load is spread out. Pass `animating`
 * for an animation frame: those canvases always draw together.
 */
export function requestCanvasRender(
  canvas?: StaticCanvas | null,
  options: { animating?: boolean } = {}
): void {
  if (!canvas) return;
  pendingRenders.add(canvas);
  if (options.animating) animatingRenders.add(canvas);
  renderFrame ||= requestAnimationFrame(renderPendingCanvases);
}

export function requestAdRender(adId: string): void {
  const store = useEditorStore();
  requestCanvasRender(store.ads[adId]?.canvas);
}

export function requestAllCanvasRenders(): void {
  const store = useEditorStore();
  for (const ad of Object.values(store.ads)) {
    requestCanvasRender(ad.canvas);
  }
}

/**
 * Sync (export / snapshot only)
 */

/**
 * Synchronous render. Use for export or snapshot paths that mutate
 * Fabric and immediately read pixels. UI updates must use the async
 * `requestCanvasRender` instead — sync renders block the event loop.
 */
export function renderCanvasNow(canvas?: Canvas | StaticCanvas | null): void {
  if (!canvas) return;
  canvas.renderAll();
}

/**
 * Helpers
 */

// renderAll also cancels any draw Fabric had queued for itself, so nothing is drawn twice
function renderPendingCanvases(frameStart: number): void {
  renderFrame = 0;
  const canvases = [...pendingRenders];
  const animating = new Set(animatingRenders);
  pendingRenders.clear();
  animatingRenders.clear();
  let drawn = 0;
  for (const canvas of canvases) {
    if (canvas.disposed) continue;
    const overBudget = performance.now() - frameStart > FRAME_BUDGET_MS;
    if (drawn > 0 && overBudget && !animating.has(canvas)) {
      pendingRenders.add(canvas);
      continue;
    }
    canvas.renderAll();
    drawn++;
  }
  if (pendingRenders.size > 0) {
    renderFrame = requestAnimationFrame(renderPendingCanvases);
  }
}
